import React, { useEffect, useState } from 'react';
import { Copy, Check, ChevronDown, Sparkles, RefreshCw, Loader2, Wand2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import ProfileScoreCard from './ProfileScoreCard';
import AuditTimeline from './AuditTimeline';
import WhyNotPotential from './WhyNotPotential';
import RebuildAuditCard from './RebuildAuditCard';
import { linkedge } from '../../lib/api';

// Legacy analyses may still carry photo/banner gap items hallucinated by the
// LLM. The source PDF contains no visuals, so we hide any gap mentioning
// profile photo, headshot, banner, cover image, etc.
const VISUAL_GAP_TERMS = [
  'headshot',
  'profile photo',
  'profile picture',
  'profile pic',
  'professional photo',
  'cover banner',
  'cover image',
  'cover photo',
  'background banner',
  'background image',
  'banner image',
  'linkedin banner',
  'branded banner',
  'branded cover',
  'header image',
  'hero image',
];

function isVisualGap(g) {
  if (!g) return false;
  const text = (typeof g === 'string'
    ? g
    : Object.values(g).filter((v) => typeof v === 'string').join(' ')
  ).toLowerCase();
  return VISUAL_GAP_TERMS.some((term) => text.includes(term));
}

function CopyButton({ text, testid = 'results-copy-section-button' }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => {
        navigator.clipboard.writeText(text || '');
        setCopied(true);
        toast.success('Copied to clipboard');
        setTimeout(() => setCopied(false), 1500);
      }}
      data-testid={testid}
    >
      {copied ? <Check size={14} className="mr-1.5 text-[hsl(var(--success))]" /> : <Copy size={14} className="mr-1.5" />}
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
}

function SectionHeader({ index, title, subtitle, copyText }) {
  return (
    <div className="flex items-start justify-between gap-3 mb-3">
      <div>
        <p className="text-xs font-mono-edge text-muted-foreground">SECTION {index}</p>
        <h3 className="text-xl font-semibold">{title}</h3>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {copyText && <CopyButton text={copyText} />}
    </div>
  );
}

function DiffPair({ current, recommended }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Card className="border-border bg-[hsl(var(--peach))]/30" data-testid="diff-current-section">
        <CardContent className="p-4">
          <p className="text-xs font-mono-edge text-muted-foreground mb-2">CURRENT</p>
          <p className="text-sm whitespace-pre-wrap">{current || '—'}</p>
        </CardContent>
      </Card>
      <Card className="border-primary/40 bg-[hsl(var(--mint))]/30" data-testid="diff-recommended-section">
        <CardContent className="p-4">
          <p className="text-xs font-mono-edge text-primary mb-2">RECOMMENDED</p>
          <p className="text-sm whitespace-pre-wrap">{recommended || '—'}</p>
        </CardContent>
      </Card>
    </div>
  );
}

function Reasoning({ text }) {
  if (!text) return null;
  return (
    <Accordion type="single" collapsible className="mt-3">
      <AccordionItem value="why" className="border-none">
        <AccordionTrigger className="text-sm py-2" data-testid="diff-reasoning-toggle">
          Why this works
        </AccordionTrigger>
        <AccordionContent className="text-sm text-muted-foreground">{text}</AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

const ABOUT_VARIANTS = [
  { key: 'default', label: 'Balanced (default)' },
  { key: 'warm', label: 'Warmer / personable' },
  { key: 'direct', label: 'Direct / declarative' },
  { key: 'thought_leader', label: 'Thought leader' },
  { key: 'concise', label: 'Concise (110-140 words)' },
];

function formatGeneratedAt(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_e) {
    return null;
  }
}

export function SuggestedAboutCard({ analysisId, suggested, isShortNotEmpty = false, onUpdate }) {
  const [current, setCurrent] = useState(suggested || null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busyVariant, setBusyVariant] = useState(null);

  if (!current?.text) return null;

  const wordCount = current.word_count || current.text.split(/\s+/).filter(Boolean).length;
  const generated = formatGeneratedAt(current.generated_at);
  const variantLabel =
    ABOUT_VARIANTS.find((v) => v.key === (current.variant || 'default'))?.label || 'Balanced';

  const handleCopy = () => {
    navigator.clipboard.writeText(current.text || '');
    setCopied(true);
    toast.success('Suggested About copied to clipboard');
    setTimeout(() => setCopied(false), 1500);
  };

  const handleRegenerate = async (variantKey) => {
    if (!analysisId) {
      toast.error('Missing analysis context — refresh and try again.');
      return;
    }
    setBusy(true);
    setBusyVariant(variantKey);
    try {
      const res = await linkedge.regenerateAbout(analysisId, { variant: variantKey });
      const next = res?.suggested_about;
      if (next?.text) {
        setCurrent(next);
        if (typeof onUpdate === 'function') onUpdate(next);
        toast.success(
          `New ${ABOUT_VARIANTS.find((v) => v.key === variantKey)?.label || 'variant'} draft ready`,
        );
      } else {
        toast.error('Could not generate a new About draft. Try again in a moment.');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Regeneration failed';
      toast.error(typeof msg === 'string' ? msg : 'Regeneration failed');
    } finally {
      setBusy(false);
      setBusyVariant(null);
    }
  };

  return (
    <Card
      className="border-primary/40 bg-[hsl(var(--mint))]/25 mb-4"
      data-testid="suggested-about-card"
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-start gap-2">
            <Sparkles size={16} className="mt-0.5 text-primary" aria-hidden="true" />
            <div>
              <p className="text-xs font-mono-edge text-primary">
                {isShortNotEmpty ? 'SUGGESTED ABOUT (CURRENT IS TOO SHORT)' : 'SUGGESTED ABOUT (NONE ON PROFILE)'}
              </p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Drafted by Claude Sonnet 4.5 from this profile&apos;s industry, seniority and experience.
                Paste directly into LinkedIn.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              data-testid="suggested-about-copy-button"
            >
              {copied ? (
                <Check size={14} className="mr-1.5 text-[hsl(var(--success))]" />
              ) : (
                <Copy size={14} className="mr-1.5" />
              )}
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  data-testid="suggested-about-regenerate-button"
                >
                  <RefreshCw
                    size={14}
                    className={`mr-1.5 ${busy ? 'animate-spin' : ''}`}
                  />
                  {busy ? 'Generating…' : 'Regenerate'}
                  <ChevronDown size={14} className="ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Pick a tone</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {ABOUT_VARIANTS.map((v) => (
                  <DropdownMenuItem
                    key={v.key}
                    disabled={busy}
                    onSelect={(e) => {
                      e.preventDefault();
                      handleRegenerate(v.key);
                    }}
                    data-testid={`suggested-about-variant-${v.key}`}
                  >
                    {busyVariant === v.key ? (
                      <RefreshCw size={12} className="mr-2 animate-spin" />
                    ) : (
                      <span className="w-3 mr-2" />
                    )}
                    {v.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <p
          className="text-sm whitespace-pre-wrap leading-relaxed"
          data-testid="suggested-about-text"
        >
          {current.text}
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs font-mono-edge text-muted-foreground">
          <Badge variant="secondary" className="font-mono-edge text-[10px]">
            {variantLabel}
          </Badge>
          <span data-testid="suggested-about-wordcount">{wordCount} words</span>
          {generated && <span>· {generated}</span>}
          {current.model && <span>· {current.model}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Per-section free-text prompt input used inside Profile Rewrite.
 * Lets the user say "please include my AWS cert" / "lean the headline
 * towards PM roles" / "weave in the Stanford Online course" and
 * triggers a focused LLM rewrite of that one section via
 * ``POST /analyze/{id}/rewrite-section``. On success the parent
 * ``onApplied(updated)`` callback merges the new content into the
 * blueprint and the UI refreshes instantly.
 */
// One-click prompt chips. Section-aware so the chip text makes sense
// in context (e.g. "Target a Director-level role" surfaces only on
// Headline / About, "Highlight team-leadership" surfaces everywhere).
// Clicking a chip pre-fills the textarea so the user can tweak the
// wording before hitting Apply.
const SUGGESTED_PROMPTS_BY_SECTION = {
  headline: [
    'Add my latest certification',
    'Target a Director-level role',
    'Highlight my team-leadership',
  ],
  about: [
    'Add my latest certification',
    'Target a Director-level role',
    'Highlight my team-leadership',
  ],
  skills: [
    'Add my latest certification',
    'Prioritize cloud and AI skills',
    'Highlight my team-leadership',
  ],
  experience: [
    'Quantify impact with metrics',
    'Highlight team-leadership in this role',
    'Add my latest certification',
  ],
};

function SectionPromptBox({ analysisId, section, experienceIndex, placeholder, label, onApplied }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);

  const effectiveLabel = label || 'Add a specific request';
  const suggestions = SUGGESTED_PROMPTS_BY_SECTION[section] || [];

  const handleSubmit = async () => {
    const text = (prompt || '').trim();
    if (!text || busy) return;
    if (!analysisId) {
      toast.error('Missing analysis context — refresh and try again.');
      return;
    }
    setBusy(true);
    try {
      const payload = { section, user_prompt: text };
      if (typeof experienceIndex === 'number') payload.experience_index = experienceIndex;
      const res = await linkedge.rewriteSection(analysisId, payload);
      if (res?.updated) {
        onApplied?.(res.updated, experienceIndex);
        toast.success('Rewrite applied');
        setPrompt('');
        setOpen(false);
      } else {
        toast.error('No rewrite returned. Try rephrasing your request.');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || 'Rewrite failed. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Rewrite failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const testBase = `section-prompt-${section}${typeof experienceIndex === 'number' ? `-${experienceIndex}` : ''}`;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-4 w-full group rounded-xl border-2 border-dashed border-primary/40 bg-primary/[0.04] hover:bg-primary/[0.08] hover:border-primary/70 px-4 py-3.5 flex items-center justify-between gap-3 transition-colors text-left"
        data-testid={`${testBase}-open`}
      >
        <span className="flex items-center gap-2.5 min-w-0">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary group-hover:bg-primary/25 transition-colors">
            <Wand2 size={15} aria-hidden="true" />
          </span>
          <span className="min-w-0">
            <span className="block text-[13.5px] font-semibold text-foreground">
              {effectiveLabel}
            </span>
            <span className="block text-[11.5px] text-muted-foreground truncate">
              Add a skill, course, or any detail you want woven in.
            </span>
          </span>
        </span>
        <ChevronDown
          size={16}
          className="text-primary shrink-0 transition-transform"
          aria-hidden="true"
        />
      </button>
    );
  }

  return (
    <Card className="mt-3 border-primary/40 bg-primary/[0.03]" data-testid={testBase}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="text-xs font-mono-edge tracking-[0.2em] uppercase text-primary">
            Specific request
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setOpen(false); setPrompt(''); }}
            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
            data-testid={`${testBase}-cancel`}
          >
            Cancel
          </Button>
        </div>

        {suggestions.length > 0 && (
          <div
            className="mb-3 flex flex-wrap gap-1.5"
            data-testid={`${testBase}-suggestions`}
            aria-label="Suggested prompts"
          >
            {suggestions.map((s, i) => (
              <button
                key={s}
                type="button"
                onClick={() => setPrompt(s)}
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-background/70 px-2.5 py-1 text-[11.5px] text-foreground/80 hover:border-primary/60 hover:bg-primary/10 hover:text-foreground transition-colors disabled:opacity-50"
                data-testid={`${testBase}-chip-${i}`}
              >
                <Sparkles size={10} className="text-primary" aria-hidden="true" />
                {s}
              </button>
            ))}
          </div>
        )}

        <Textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={placeholder || 'e.g. Please include my AWS Solutions Architect certification completed in Dec 2025.'}
          rows={3}
          maxLength={1200}
          className="resize-none text-sm"
          data-testid={`${testBase}-input`}
        />
        <div className="mt-2 flex items-center justify-between gap-3">
          <p className="text-[11px] text-muted-foreground">
            Only this section rewrites — your other sections stay as they are.
          </p>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={!prompt.trim() || busy}
            data-testid={`${testBase}-submit`}
          >
            {busy ? (
              <>
                <Loader2 size={13} className="mr-1.5 animate-spin" />
                Rewriting…
              </>
            ) : (
              <>
                <Sparkles size={13} className="mr-1.5" />
                Apply
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ProfileOptimizer({ data }) {
  // Local blueprint state so per-section "Prompt submission" rewrites
  // can refresh just the affected slice without a full page reload.
  // Seeded from the server payload, then mutated by ``applyRewrite``
  // whenever the user submits a prompt below any section.
  const [bp, setBp] = useState(data.blueprint || {});
  useEffect(() => {
    setBp(data.blueprint || {});
  }, [data?.id, data?.blueprint]);

  const scores = bp.scores || {};
  const headline = bp.headline || {};
  const about = bp.about || {};
  const skills = bp.skills || {};
  const experiences = bp.experiences || [];
  const gaps = (bp.gaps || []).filter((g) => !isVisualGap(g));
  const scoring = data.profile_scores;

  const applyRewrite = (section, updated, experienceIndex) => {
    setBp((prev) => {
      const next = { ...prev };
      if (section === 'experience') {
        const list = [...(prev.experiences || [])];
        if (typeof experienceIndex === 'number' && experienceIndex >= 0 && experienceIndex < list.length) {
          list[experienceIndex] = { ...list[experienceIndex], ...updated };
          next.experiences = list;
        }
      } else {
        next[section] = { ...(prev[section] || {}), ...updated };
      }
      return next;
    });
  };

  // Rebuild-missing state is now owned by <RebuildAuditCard /> — no
  // local state needed here anymore. Retain the "empty blueprint"
  // predicate locally so the rewrite sections stay hidden while the
  // recovery card is showing instead of rendering a stack of "—".
  const rewriteMissing =
    !headline.variants?.length &&
    !about.suggested &&
    !about.current &&
    (!experiences || experiences.length === 0) &&
    (!skills || (!skills.suggested?.length && !skills.priority?.length));

  return (
    <div className="space-y-8">
      {/* New: Profile score card (current vs potential, 0-100 with 5 categories) */}
      {scoring ? (
        <ProfileScoreCard scoring={scoring} analysis={data} />
      ) : (        scores && Object.keys(scores).length > 0 && (
          <Card className="border-border" data-testid="profile-scores-legacy">
            <CardContent className="p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold">Profile scores</h3>
                  <p className="text-sm text-muted-foreground">Assessed against Blayzrr&apos;s objective-specific Profile Intelligence Framework.</p>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="profile-scores">
                  {Object.entries(scores).map(([k, v]) => (
                    <Badge key={k} variant="secondary" className="px-3 py-1 font-mono-edge text-sm">
                      {k}: <span className="ml-1 font-semibold text-primary">{v}/10</span>
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )
      )}

      {/* Blueprint-missing recovery — same amber-highlighted animated
          card as the v2 overview, kept in the legacy layout too. */}
      <RebuildAuditCard data={data} />

      {/* Audit timeline — score trajectory across multiple audits for this profile */}
      <AuditTimeline profileName={data?.profile_name} currentId={data?.id} />

      {/* Why didn't I hit potential? — per-category gap analysis */}
      {scoring && <WhyNotPotential scoring={scoring} />}

      {/* Key gaps callout */}
      {gaps.length > 0 && (
        <Card className="border-border" data-testid="profile-gaps-card">
          <CardContent className="p-5">
            <p className="text-xs font-mono-edge text-muted-foreground mb-2">KEY GAPS TO CLOSE</p>
            <ul className="text-sm space-y-1.5">
              {gaps.slice(0, 6).map((g, i) => (
                <li key={i} className="flex gap-2"><span className="text-primary">•</span> {g}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Rewrite sections — hidden entirely when the blueprint is empty so
          the user sees the recovery card instead of a stack of "—"
          placeholders. */}
      {!rewriteMissing && (
        <>
      {/* 1. Headline */}
      <section data-testid="section-headline">
        <SectionHeader index={1} title="Headline" subtitle="3 variants, each tuned for a different use case." />
        <Card className="border-border bg-[hsl(var(--peach))]/30 mb-3">
          <CardContent className="p-4">
            <p className="text-xs font-mono-edge text-muted-foreground mb-2">CURRENT</p>
            <p className="text-sm whitespace-pre-wrap">{headline.current || '—'}</p>
          </CardContent>
        </Card>
        <div className="grid md:grid-cols-3 gap-3">
          {(headline.variants || []).map((v, i) => (
            <Card key={i} className="border-primary/40 bg-card" data-testid={`headline-variant-${i}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Badge className="bg-primary text-primary-foreground">{v.use_case}</Badge>
                  <CopyButton text={v.text} testid={`headline-variant-copy-${i}`} />
                </div>
                <p className="text-sm whitespace-pre-wrap">{v.text}</p>
                {v.keywords && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {v.keywords.map((k, j) => (
                      <Badge key={j} variant="secondary" className="font-mono-edge text-[10px]">{k}</Badge>
                    ))}
                  </div>
                )}
                <p className="text-xs font-mono-edge text-muted-foreground mt-3">{(v.text || '').length}/220 chars</p>
              </CardContent>
            </Card>
          ))}
        </div>
        <Reasoning text={headline.reasoning} />
        <SectionPromptBox
          analysisId={data.id}
          section="headline"
          placeholder="e.g. Lean the headline towards product management roles at Series-B SaaS companies."
          label="Add a specific request for the headline"
          onApplied={(updated) => applyRewrite('headline', updated)}
        />
      </section>

      {/* 2. About */}
      <section data-testid="section-about">
        <SectionHeader index={2} title="About / Summary" copyText={about.recommended} />
        {/* When the audited profile has no About on file (or it's too thin
            to be useful — under 100 chars), surface the Claude-generated
            suggested draft as a dedicated card BEFORE the standard
            current/recommended diff pair. This makes it unambiguous that
            the user has a ready-to-paste draft. */}
        {(() => {
          const currentAbout = (about.current || '').trim();
          const isMissing = !currentAbout;
          const isShort = !isMissing && currentAbout.length < 100;
          const showSuggested = (isMissing || isShort) && !!data.suggested_about?.text;
          if (!showSuggested) return null;
          return (
            <SuggestedAboutCard
              analysisId={data.id}
              suggested={data.suggested_about}
              isShortNotEmpty={isShort}
            />
          );
        })()}
        <DiffPair current={about.current} recommended={about.recommended} />
        {about.embedded_keywords && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {about.embedded_keywords.map((k, i) => (
              <Badge key={i} variant="secondary" className="font-mono-edge text-xs">{k}</Badge>
            ))}
          </div>
        )}
        <Reasoning text={about.reasoning} />
        <SectionPromptBox
          analysisId={data.id}
          section="about"
          placeholder="e.g. Please weave in the Stanford Online course I just finished on Operations Research."
          label="Add a specific request for the About"
          onApplied={(updated) => applyRewrite('about', updated)}
        />
      </section>

      {/* 3. Skills */}
      <section data-testid="section-skills">
        <SectionHeader index={3} title="Skills" copyText={(skills.recommended || []).join(', ')} />
        <Card className="border-border">
          <CardContent className="p-5 space-y-4">
            <div>
              <p className="text-xs font-mono-edge text-primary mb-2">RECOMMENDED (PRIORITIZED)</p>
              <div className="flex flex-wrap gap-2">
                {(skills.recommended || []).map((s, i) => (
                  <Badge key={i} variant="secondary" className="text-sm">{s}</Badge>
                ))}
              </div>
            </div>
            {skills.remove_or_deprioritize && skills.remove_or_deprioritize.length > 0 && (
              <div>
                <p className="text-xs font-mono-edge text-muted-foreground mb-2">REMOVE / DEPRIORITIZE</p>
                <div className="flex flex-wrap gap-2">
                  {skills.remove_or_deprioritize.map((s, i) => (
                    <Badge key={i} className="text-sm bg-[hsl(var(--peach))]/40 text-foreground border border-border line-through">{s}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        <Reasoning text={skills.reasoning} />
        <SectionPromptBox
          analysisId={data.id}
          section="skills"
          placeholder="e.g. Please add AWS Solutions Architect, Snowflake and Terraform to the recommended list."
          label="Add a specific request for Skills"
          onApplied={(updated) => applyRewrite('skills', updated)}
        />
      </section>

      {/* 4. Experience */}
      <section data-testid="section-experience">
        <SectionHeader index={4} title="Work experience (rewritten)" />
        <div className="space-y-4">
          {experiences.map((e, i) => {
            const bullets = (e.recommended_bullets || []).join('\n• ');
            const copyText = `${e.title} — ${e.company} (${e.dates})\n• ${bullets}`;
            return (
              <Card key={i} className="border-border" data-testid={`experience-${i}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h4 className="font-semibold">{e.title} — {e.company}</h4>
                      <p className="text-xs text-muted-foreground">{e.dates}</p>
                    </div>
                    <CopyButton text={copyText} testid={`experience-copy-${i}`} />
                  </div>
                  <div className="mt-4 grid md:grid-cols-2 gap-3">
                    <Card className="border-border bg-[hsl(var(--peach))]/30">
                      <CardContent className="p-3">
                        <p className="text-xs font-mono-edge text-muted-foreground mb-2">CURRENT</p>
                        <p className="text-sm whitespace-pre-wrap">{e.current_description || '—'}</p>
                      </CardContent>
                    </Card>
                    <Card className="border-primary/40 bg-[hsl(var(--mint))]/30">
                      <CardContent className="p-3">
                        <p className="text-xs font-mono-edge text-primary mb-2">RECOMMENDED</p>
                        <ul className="text-sm space-y-1.5">
                          {(e.recommended_bullets || []).map((b, j) => (
                            <li key={j} className="flex gap-2"><span className="text-primary">•</span> {b}</li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                  <Reasoning text={e.reasoning} />
                  <SectionPromptBox
                    analysisId={data.id}
                    section="experience"
                    experienceIndex={i}
                    placeholder="e.g. Please mention I led the migration to AWS and reduced costs by 30%."
                    label="Add a specific request for this role"
                    onApplied={(updated) => applyRewrite('experience', updated, i)}
                  />
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>
        </>
      )}
    </div>
  );
}
